import React from 'react';
import './MetricStat.css';

/**
 * Period-over-period KPI tile used on the Home customer cards. Renders the
 * current-window number, colored by trend vs the previous window, plus a small
 * up/flat/down arrow that tooltips the previous value.
 *
 * Trend rule (mirrors backend): >5% above prev = up/green, <5% below = down/red,
 * within +/-5% (or both zero) = flat/yellow. prev=0 with curr>0 is treated as up.
 */
function classifyTrend(current, previous) {
  const c = Number(current) || 0;
  const p = Number(previous) || 0;
  if (p === 0) {
    if (c === 0) return 'flat';
    return 'up';
  }
  const pct = (c - p) / p;
  if (pct > 0.05) return 'up';
  if (pct < -0.05) return 'down';
  return 'flat';
}

function ArrowUp() {
  return (
    <svg viewBox="0 0 12 12" className="metric-arrow" aria-hidden="true">
      <path d="M6 1.5 L11 8 L7.5 8 L7.5 11 L4.5 11 L4.5 8 L1 8 Z" fill="currentColor" />
    </svg>
  );
}

function ArrowFlat() {
  return (
    <svg viewBox="0 0 12 12" className="metric-arrow" aria-hidden="true">
      <path d="M1.5 4.5 L8 4.5 L8 2.5 L11 6 L8 9.5 L8 7.5 L1.5 7.5 Z" fill="currentColor" />
    </svg>
  );
}

function ArrowDown() {
  return (
    <svg viewBox="0 0 12 12" className="metric-arrow" aria-hidden="true">
      <path d="M6 10.5 L1 4 L4.5 4 L4.5 1 L7.5 1 L7.5 4 L11 4 Z" fill="currentColor" />
    </svg>
  );
}

function MetricStat({ label, current, previous, currentLabel, previousLabel, loading = false }) {
  if (loading) {
    return (
      <div className="metric-stat metric-stat-loading" aria-busy="true" aria-label={`Loading ${label}`}>
        <span className="metric-stat-label">{label}</span>
        <span className="metric-stat-value-row">
          <span className="metric-stat-shimmer" aria-hidden="true" />
        </span>
      </div>
    );
  }

  const trend = classifyTrend(current, previous);
  const formatted = Number(current ?? 0).toLocaleString('en-US');
  const previousFormatted = Number(previous ?? 0).toLocaleString('en-US');

  // Tooltip is the primary affordance for "where do these colors come
  // from?" — show both windows with their human-readable date ranges so
  // hovering any tile gives the full comparison context.
  const lines = [];
  if (currentLabel) lines.push(`Current (${currentLabel}): ${formatted}`);
  if (previousLabel) lines.push(`Previous (${previousLabel}): ${previousFormatted}`);
  if (lines.length === 0) lines.push(`Previous: ${previousFormatted}`);
  const tooltip = lines.join('\n');

  return (
    <div className={`metric-stat metric-${trend}`}>
      <span className="metric-stat-label">{label}</span>
      <span className="metric-stat-value-row">
        <span className="metric-stat-value">{formatted}</span>
        <span className="metric-stat-arrow" title={tooltip} aria-label={tooltip}>
          {trend === 'up' && <ArrowUp />}
          {trend === 'flat' && <ArrowFlat />}
          {trend === 'down' && <ArrowDown />}
        </span>
      </span>
    </div>
  );
}

export default MetricStat;
